import {Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import {RondeivuHttpClient} from "../../handlers";
import {Store} from "@ngrx/store";
import { toggleLoading } from "../../modules/redux";
import { AuthResponseModel, CognitoLoginTokenModel, IAppUser, IInviteTokenDto, IRegisterAppUser, IRegistrationInviteDto, LoginResponseModel, VerifyEmailModel } from 'src/app/models/i-app-user';
import {ToastrService} from "ngx-toastr";
import { ActivatedRoute, Params, Router, UrlTree } from '@angular/router';
import { ValidatorFn, Validators } from '@angular/forms';
import { catchError, finalize, firstValueFrom, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { CoreService } from '../core.service';
import { IBusinessUnit } from 'src/app/modules/business-units/models';
import { BusinessUnitService } from 'src/app/modules/business-units/services';
import { SentryService } from '../sentry.service';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly BASE_URL = environment.rondeivu_api;

  // api routes
  public readonly REGISTER_FRAGMENT = '/auth/self-register';
  public readonly REGISTER_INVITE_FRAGMENT = '/auth/invite-register';
  public readonly LOGIN_FRAGMENT = '/auth/login';
  public readonly VERIFY_EMAIL_FRAGMENT = '/auth/verify-email';
  public readonly RESEND_CONFIRMATION_FRAGMENT = '/auth/resend-email-code';
  public readonly VERIFY_SMS_FRAGMENT = '/auth/verify-sms-code';
  // public readonly RESEND_SMS_FRAGMENT = '/auth/resend-sms-code'; // check this API endpoint

  public readonly FORGOT_PASSWORD_FRAGMENT = '/auth/send-forgot-password-code';
  public readonly RESEND_FORGOT_PASSWORD_FRAGMENT = '/auth/resend-forgot-password-code'; // check this API endpoint
  public readonly CONFIRM_FORGOT_PASSWORD_FRAGMENT = '/auth/verify-forgot-password-code';


  public readonly SET_NEW_PASSWORD_AFTER_CHALLENGE_FRAGMENT = '/auth/new-password-required'; // this is the new password required endpoint

  public readonly CHANGE_PASSWORD_FRAGMENT = '/auth/change-password'; // this is the user change password endpoint
  public readonly LOGOUT_FRAGMENT = '/auth/logout';

  

  // routes
  public readonly REGISTER_ROUTE = '/auth/register';
  public readonly LOGIN_ROUTE = '/auth/login';
  public readonly MFA_CONFIRM_ROUTE = '/auth/mfa';
  public readonly EMAIL_CONFIRM_ROUTE = '/auth/verify-email';
  public readonly RESET_PASSWORD_ROUTE = '/auth/reset-password';
  public readonly PASSWORD_CHALLENGE_ROUTE = '/auth/reset-password?action=set-new-password';
  public readonly FORCE_CHANGE_PASSWORD_ROUTE = '/auth/reset-password?action=new-password-required';

  // password pattern validator
  public readonly passwordPatternValidator: ValidatorFn = Validators.pattern("^(?=.*[`!@#$%^&*()_+\\-=\\[\\]{};':\\\\|,.<>\\/?~])(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{8,}$");
 
  // private userLoginEmail: string | null = null;
  // private userLoginPassword: string | null = null;
  private userLoginDetails: { email: string; password: string, rememberDevice: boolean } | null = null;
  public appUser: IAppUser | null = null;
  private loginResponse: LoginResponseModel | null = null;
  public queryParams?: Params;

  private refreshTokenTimeout: any;



  constructor(
    private http: RondeivuHttpClient, 
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private router: Router,
    private store: Store,
    private coreService: CoreService,
    private businessUnitService: BusinessUnitService,
    private sentryService: SentryService,
    private dialog: MatDialog
    ) {
  }

  public IsLoggedIn() : Observable<boolean> {
    let accessToken = localStorage.getItem('rondeivu-accessToken');

    if (!accessToken)
      return of(false);

    // check if the token is expired
    this.queryParams = this.activatedRoute.snapshot.queryParams;

    // if there is a token, check if it's expired
    return this.ValidateToken();
  }

  public GetToken() : string | null {
    let accessToken = localStorage.getItem('rondeivu-accessToken');
    return accessToken;
  }

  public SetToken(token: CognitoLoginTokenModel | null) {
    // console.log("------> Setting token", token);
    if (!token) {
      localStorage.removeItem('rondeivu-accessToken');
      localStorage.removeItem('rondeivu-idToken');
      localStorage.removeItem('rondeivu-refreshToken');

      // also remove any cached user info
      this.appUser = null;
      this.loginResponse = null;

      // clear any existing refresh token timeout
      if (this.refreshTokenTimeout) {
        clearTimeout(this.refreshTokenTimeout);
        this.refreshTokenTimeout = null;
      }
    }
    else {
      localStorage.setItem('rondeivu-accessToken', token.accessToken || '');
      localStorage.setItem('rondeivu-idToken', token.idToken || '');
      localStorage.setItem('rondeivu-refreshToken', token.refreshToken || '');


      // set up automatic token refresh based on token expiration (assuming the token has an 'expiresIn' property in seconds)
      if (token.expiresIn) {
        // const refreshTimeSeconds = token.expiresIn - 60; // refresh 1 minute before expiration  
        const refreshTimeMilliseconds = 15 * 60 * 1000; // refresh every 15
        // console.log(`Setting token refresh timeout for ${refreshTimeSeconds}  (token expires in ${token.expiresIn} s)`);
        
        // clear the refresh token timeout if it already exists to avoid multiple timeouts running
        if (this.refreshTokenTimeout) {
          clearTimeout(this.refreshTokenTimeout);
        }
        
        // set a timeout to refresh the token
        this.refreshTokenTimeout = setTimeout(() => {
          console.log("Access token is about to expire, refreshing token...");
          this.RefreshToken().subscribe({
            next: (res) => {
              console.log("Token refreshed successfully", res);   
            },
            error: (err) => {
              console.error("Error refreshing token", err);
            }
          });
        }, refreshTimeMilliseconds);
      }
    }
  }
 
  public RegisterUser(user: IRegisterAppUser) : Observable<any> {
    // console.log("Register user", user);
    this.queryParams = this.activatedRoute.snapshot.queryParams;

    // @todo this is temporary until we have proper mobile and country code fields
    let payload = user;

    this.store.dispatch(toggleLoading({loading: true}));
    return this.http.post(this.BASE_URL + this.REGISTER_FRAGMENT, payload).pipe(
      tap((res: any) => {
        // console.log("User registered successfully", res);
        this.toastrService.success('Registration successful! Please check your email for the confirmation code.', 'Success');

        this.userLoginDetails = {
          email: user.email,
          password: user.password,
          rememberDevice: false
        };

        // redirect
        this.router.navigateByUrl(this.EMAIL_CONFIRM_ROUTE);
      }),
      catchError((err: any) => {
        console.error("Error registering user", err);
        this.toastrService.error('Registration failed. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.store.dispatch(toggleLoading({loading: false})) )
    );
  }

  public RegisterInvitedUser(user: IRegistrationInviteDto, token: string, tokenDecoded?: IInviteTokenDto) : Observable<any> {
    // console.log("Register invited user", user, token);
    this.queryParams = this.activatedRoute.snapshot.queryParams;


    // @todo this is temporary until we have proper mobile and country code fields
    let payload = {
      ...user,
      inviteToken: token,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    this.store.dispatch(toggleLoading({loading: true}));
    return this.http.post(this.BASE_URL + this.REGISTER_INVITE_FRAGMENT, payload).pipe(
      tap((res: any) => {
        // console.log("User registered successfully", res);
        this.toastrService.success('Registration successful! Please check your email for the confirmation code.', 'Success');

        this.userLoginDetails = {
          email: user.email,
          password: user.password,
          rememberDevice: false
        };
       
        // @todo: redirect to confirm page
        this.router.navigateByUrl(this.EMAIL_CONFIRM_ROUTE);
      }),
      catchError((err: any) => {
        console.error("Error registering user", err);
        this.toastrService.error('Registration failed. Please try again later.', 'Error');
        this.store.dispatch(toggleLoading({loading: false}));
        return throwError(() => err);
      }),
      finalize(() => this.store.dispatch(toggleLoading({loading: false})) )
    );  
  }

  public LoginUserAndRedirect(email: string, password: string, rememberDevice: boolean = false) {
    try {
      this.queryParams = this.activatedRoute.snapshot.queryParams;
      
      this.store.dispatch(toggleLoading({loading: true}));

      var payload = {
        email: email,
        password: password,
        rememberDevice: rememberDevice
      }

      this.http.post<LoginResponseModel>(this.BASE_URL + this.LOGIN_FRAGMENT, payload).subscribe({
        next: (res: LoginResponseModel) => {
          // console.log("Login API response", res);

          // store login information
          this.userLoginDetails = {
            email: email,
            password: password,
            rememberDevice: rememberDevice
          };

          // handle errors, success = false, and challenges in a centralized way
          this.HandleLoginChallenges(res);
          
        },
        error: (err: any) => {
          this.store.dispatch(toggleLoading({loading: false}));
          console.error("Error logging in user", err);
          this.toastrService.error('Login failed. Please try again later.', 'Login Error');
        }
      });

    } catch (error: any) {
      this.store.dispatch(toggleLoading({loading: false}));
      console.error('Error during login process:', error);
      this.toastrService.error('An unexpected error occurred during login. Please try again later.', 'Login Error');
    }
  } 

  private HandleLoginChallenges(res: LoginResponseModel) {
    // Handle special challenges (MFA, new password, etc.)
    this.loginResponse = res;

    setTimeout(() => {
      this.store.dispatch(toggleLoading({loading: false}));
    }, 500); // add a slight delay to ensure the loading spinner is visible for at least a short moment
    
    if (res.isSuccess === true) {
      // successful login

      // Handle special challenges (MFA, new password, etc.)
      if (!!res.challengeName) {
        switch (res.challengeName) {
          case 'SMS_MFA':
          case 'SOFTWARE_TOKEN_MFA':
            console.log('MFA required');
            this.router.navigateByUrl(this.MFA_CONFIRM_ROUTE);
            return;

          case 'NEW_PASSWORD_REQUIRED':
            console.log('User must set a new password');
            this.router.navigateByUrl(this.PASSWORD_CHALLENGE_ROUTE);
            return;
          case 'MFA_SETUP':
          default:
            console.log('MFA setup required');
            this.store.dispatch(toggleLoading({loading: false}));
            return;
        }
      }
      else {
        // no challenges, proceed to store tokens and redirect
        // console.log('Login successful', res);
        // this.toastrService.success('Login successful!', 'Success');

        this.SetToken(res.tokens);

        // remove the login details from memory for security purposes
        this.userLoginDetails = null;
        
        // redirect user
        this.RedirectAfterLogin().then(() => {
          console.log("Redirect after login successful");
        });
      }
      return;
    }
    else {
      switch (res.loginStatus) {
        case 'UserNotFound':
          console.error('User does not exist.');
          this.toastrService.error(res.message ?? 'User does not exist.', 'Login Error');
          break;
        case 'NotAuthorized':
          console.error('Incorrect username or password.');
          this.toastrService.error(res.message ?? 'Incorrect username or password.', 'Login Error');
          break;
        case 'UserNotConfirmed':
          console.error('User not confirmed. Please verify your account.', res.message);
          this.router.navigate([this.EMAIL_CONFIRM_ROUTE], { queryParams: { action: 'send-code' } });
          break;
        case 'PasswordResetRequired':
          console.error('Password reset required for this user.');
          this.toastrService.info(res.message ?? 'Password reset required. Please set a new password.', 'Info');
          this.router.navigateByUrl(this.FORCE_CHANGE_PASSWORD_ROUTE);
          break;
        case 'InvalidParameter':
          console.error('Invalid input. Check your username or password.');
          this.toastrService.error(res.message ?? 'Invalid input. Check your username or password.', 'Login Error');
          break;
        case 'TooManyFailedAttempts':
          console.error('Too many failed login attempts. Try again later.');
          this.toastrService.error('Too many failed login attempts. Try again later.', 'Login Error');
          break;
        case 'TooManyRequests':
          console.error('Too many requests. Please wait and try again.');
          this.toastrService.error('Too many requests. Please wait and try again.', 'Login Error');
          break;
        case 'NetworkError':
          console.error('Network error. Check your internet connection.');
          this.toastrService.error('Network error. Check your internet connection or corporate network filters.', 'Login Error');
          break;
      }
    }
  }

  public TryReloadUser() {
    var actionRoute = this.activatedRoute.snapshot.queryParamMap.get('action');
    if (actionRoute !== "noredirect") {
      try {
        console.log("Trying to reload user session");
        this.IsLoggedIn().subscribe({
          next: (isLoggedIn: boolean) => {
            if (isLoggedIn) {
              console.log("User is logged in, redirecting after reload");
              // start loader

              this.coreService.loader();
              // redirect user
              this.RedirectAfterLogin().finally(() => {
                setTimeout(() => {
                  this.coreService.loader(false);
                }, 500); // add a slight delay to ensure the loading spinner is visible for at least a short moment
              });
            }
          }
        });
      }
      catch (ex) {
        console.error("Error reloading user session", ex);
        this.coreService.loader(false);
      }
    }
  }

  public VerifyTokenExpiry() : boolean {
    // console.log("-----> Verifying token expiry");
    var token = this.GetToken();
    if (!token) {
      console.warn("No access token found during token expiry verification.");
      return false;
    }
    else {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expires = payload.exp * 1000;

      const timeout = expires - Date.now();
      console.log(`Token expires in ${timeout} ms`);

      return true;
    }
  }

  public ConfirmUserViaCode(code: string) {
    var email = this.getUserEmail() || '';
    console.log("Confirming user via code", email, code); 
    
    var payload: VerifyEmailModel = {
      email: email,
      code: code
    };

    this.coreService.loader();
    this.http.post<LoginResponseModel>(this.BASE_URL + this.VERIFY_EMAIL_FRAGMENT, payload).subscribe({
        next: (res: LoginResponseModel) => {
          console.log("Confirm email API response", res);
          this.coreService.loader(false);

          if (res.isSuccess) {
            this.toastrService.success('Email verified successfully!', 'Success');

            // if the email was successfully verified, then try to login the user
            if (!this.userLoginDetails) {
              console.warn("No stored user login details found, cannot auto-login user after email confirmation.");
              this.navigateToLogin();
              return;
            } 

            this.LoginUserAndRedirect(this.userLoginDetails.email, this.userLoginDetails.password, this.userLoginDetails.rememberDevice);
          }
          else {
            this.toastrService.error(res.message ?? 'Error verifying email. Please try again later.', 'Error');
            this.coreService.loader(false);
          }
        },
        error: (err: any) => {
          console.error("Error confirming email", err);
          this.toastrService.error('Error verifying email. Please try again later.', 'Error');
          this.coreService.loader(false);
        }
    });
  }

  public RequestEmailVerificationCode() : Promise<void> {
    var email = this.getUserEmail() || '';
    console.log("Resending email verification code to", email);
    return firstValueFrom(
      this.http.post(this.BASE_URL + this.RESEND_CONFIRMATION_FRAGMENT, { email: email }).pipe(
        map(() => void 0)
      )
    );
  }

  /// User requests a password reset via providing an email address
  public ForgotPassword(email: string) : Observable<any> {
    this.coreService.loader();
    return this.http.post(this.BASE_URL + this.FORGOT_PASSWORD_FRAGMENT, { email }).pipe(
      tap((res: any) => {
        console.log("Forgot password API response", res);
        this.toastrService.success('Password reset code sent! Please check your email.', 'Success');
      }),
      catchError((err: any) => {
        console.error("Error requesting password reset", err);
        this.toastrService.error('Error requesting password reset. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }

  public ResendForgotPasswordCode(email: string) : Observable<void> {
    this.coreService.loader();
    return this.http.post(this.BASE_URL + this.RESEND_FORGOT_PASSWORD_FRAGMENT, { email: email }).pipe(
      tap(() => {
        this.toastrService.success('Password reset code resent! Please check your email.', 'Success');
      }),
      map(() => void 0),
      catchError((err: any) => {
        console.error("Error resending password reset code", err);
        this.toastrService.error('Error resending password reset code. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }

  /// This is the second step where user provides the code received via email and the new password
  public ConfirmForgotPassword(email: string, code: string, newPassword: string) : Observable<LoginResponseModel> { 
    this.coreService.loader();
    return this.http.post<LoginResponseModel>(this.BASE_URL + this.CONFIRM_FORGOT_PASSWORD_FRAGMENT, { email: email, confirmationCode: code, newPassword: newPassword }).pipe(
      tap((res: LoginResponseModel) => {
        console.log("Reset password API response", res);

        if (res.isSuccess) {
          this.toastrService.success('Password reset successful! You can now log in with your new password.', 'Success');
        }
        else {
          this.toastrService.error(res.message ?? 'Error resetting password. Please try again later.', 'Error');
        }
      }),
      catchError((err: any) => {
        console.error("Error resetting password", err);
        this.toastrService.error('Error resetting password. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }

  public SetNewPasswordAfterChallenge(newPassword: string) : Observable<any> {
    var payload = {
      sessionId: this.loginResponse?.sessionId,
      email: this.getUserEmail(),
      newPassword: newPassword,
    }

    console.log("Setting new password after challenge with payload", payload);

    this.coreService.loader();
    return this.http.post<LoginResponseModel>(this.BASE_URL + this.SET_NEW_PASSWORD_AFTER_CHALLENGE_FRAGMENT, payload).pipe(
      tap((res: LoginResponseModel) => {
        console.log("Change password API response", res);
        this.toastrService.success('Password changed successfully! You can now log in with your new password.', 'Success');
        this.HandleLoginChallenges(res);
      }),
      catchError((err: any) => {
        console.error("Error changing password", err);
        this.toastrService.error('Error changing password. Please try again later.', 'Error');
        // take the user back to login
        this.navigateToLogin();
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }

  public ConfirmMFA(code: string) : Observable<LoginResponseModel> {
    var payload = {
      sessionId: this.loginResponse?.sessionId,
      email: this.getUserEmail(),
      code: code
    };

    this.coreService.loader();
    return this.http.post<LoginResponseModel>(this.BASE_URL + this.VERIFY_SMS_FRAGMENT, payload).pipe(
      tap((res: LoginResponseModel) => {
        console.log("MFA confirmation API response", res);  
        if (res.isSuccess) {
          this.toastrService.success('MFA verification successful!', 'Success');
          this.HandleLoginChallenges(res);
        }
        else {
          this.toastrService.error('MFA verification failed. Please try again.', 'Error');
        }
      }),
      catchError((err: any) => {
        console.error("Error confirming MFA", err); 
        this.toastrService.error('Error confirming MFA. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }

  public ResendMFACode() : Observable<any> {
    var email = this.getUserEmail() || '';
    console.log("Resending MFA code to", email);
    try {
      // this.coreService.loader();

      if (this.userLoginDetails == null) {
        // this.coreService.loader(false);
        console.error("No stored user login details found, cannot resend MFA code.");
        this.toastrService.error('Unable to resend MFA code. Please log in again.', 'Error');
        this.navigateToLogin();
        return of(void 0);
      }
      else if (!this.userLoginDetails.email || !this.userLoginDetails.password) {
        // no email or password in the stored login details, cannot resend MFA code
        this.toastrService.error("An error occured, please login again.", "Error");
        this.navigateToLogin();
        return of(void 0);
      }


      this.coreService.loader();
      return this.http.post<LoginResponseModel>(this.BASE_URL + this.LOGIN_FRAGMENT, this.userLoginDetails).pipe(
        tap((res: LoginResponseModel) => {
          console.log("Resend MFA code API response", res); 
          this.loginResponse = res;

          if (res.isSuccess) {
            this.toastrService.success('MFA code resent successfully!', 'Success');
          }
          else {
            this.toastrService.error(res.message ?? 'Error resending MFA code. Please try again later.', 'Error');
            this.navigateToLogin();
          }
        }),
        map(() => void 0),
        catchError((err: any) => {
          console.error("Error resending MFA code", err); 
          this.toastrService.error('Error resending MFA code. Please try again later.', 'Error');
          return throwError(() => err);
        }),
        finalize(() => this.coreService.loader(false))
      );

    } catch (error: any) {
      this.coreService.loader(false);
      console.error('Error during login process:', error);
      this.toastrService.error('An unexpected error occurred during login. Please try again later.', 'Login Error');
      return of(void 0);
    }
  }

  /// This function changes the password for the logged-in user (a token is required for this to take place)
  public ChangePassword(currentPassword: string, newPassword: string) : Observable<any> { 
    this.coreService.loader();
    return this.http.post(this.BASE_URL + this.CHANGE_PASSWORD_FRAGMENT, { currentPassword, newPassword }).pipe(
      tap((res: any) => {
        console.log("Change password API response", res);
        if (res.isSuccess) {
          this.toastrService.success('Password changed successfully!', 'Success');
        }
        else {
          this.toastrService.error(res.message ?? 'Error changing password. Please try again later.', 'Error');
        }
      }),
      catchError((err: any) => {
        console.error("Error changing password", err);
        this.toastrService.error('Error changing password. Please try again later.', 'Error');
        return throwError(() => err);
      }),
      finalize(() => this.coreService.loader(false))
    );
  }


  public ValidateToken() : Observable<boolean> {
    // check if there is a token first
    var token = this.GetToken();
    if (!token) {
      console.warn("No access token found during token validation.");
      return of(false);
    }

    return this.http.get<AuthResponseModel>(this.BASE_URL + '/auth/validate').pipe(
      map((res: AuthResponseModel) => {
        console.log("Token validation response", res);
        return res.isSuccess;
      }),
      catchError((err: any) => {
        console.error("Token validation error", err); 
        return of(false);
      })
    );
  }


  public ValidateAndRefreshToken() : Observable<boolean> {
    return this.http.get<AuthResponseModel>(this.BASE_URL + '/auth/validate').pipe(
      switchMap((res: AuthResponseModel) => {
        console.log("Token validation response", res);
        if (res.isSuccess) {
          return of(true);
        }

        return this.RefreshToken().pipe(
          map((refreshRes: LoginResponseModel) => {
            console.log("Token refresh response during validate and refresh", refreshRes);  
            return refreshRes.isSuccess;
          }),
          catchError((err: any) => {
            console.error("Error refreshing token during validate and refresh", err);
            return of(false);
          })
        );
      }),
    );
  }

  public RefreshToken() : Observable<LoginResponseModel> {
    var refreshToken = localStorage.getItem('rondeivu-refreshToken');

    if (!refreshToken) {
      console.log("No refresh token available");
      return throwError(() => new Error("No refresh token available"));
    }

    return this.http.post<LoginResponseModel>(this.BASE_URL + '/auth/refresh', { refreshToken })
      .pipe(tap((res: LoginResponseModel) => {
        console.log("Token refreshed response", res);
        if (res.isSuccess) {
          this.loginResponse = res;
          this.SetToken(res.tokens);
        }
        else {
          console.error("Failed to refresh token", res);
          this.toastrService.error('Failed to refresh session. Please log in again.', 'Error');
          this.Logout();
          this.navigateToLogin();
        }
      }),
      catchError(error => {
        console.error("Error refreshing token", error);
        this.toastrService.error('Failed to refresh session. Please log in again.', 'Error');
        this.Logout();
        this.navigateToLoginWithNoRedirect();
        return throwError(() => error);
      })
    );
  }

  public RedirectAfterLogin() : Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.GetRedirectUrlAfterLogin(this.queryParams).subscribe({
        next: (urlTree: UrlTree) => {
          console.log("Redirecting to URL after login", urlTree.toString());
          this.router.navigateByUrl(urlTree).then(resolve).catch(reject);
        },
        error: (err: any) => {
          console.error("Error getting redirect URL after login", err);
          // this.toastrService.error('Error during login redirection. Please try again later.', 'Error');
          this.navigateToLoginWithNoRedirect().subscribe(reject);
        }
      });
    });
  }

  private GetRedirectUrlAfterLogin(queryParams?: Params): Observable<UrlTree> {
    console.log("-----> Getting redirect URL after login with query params: ", queryParams, !queryParams);
    return this.businessUnitService.getUserBusinessUnits().pipe(
      map((bus: IBusinessUnit[]) => {
        console.log("Business units for user", bus);
        if (bus.length == 0) {
          this.toastrService.info("No Business Units assigned to your user account. Please contact support.");
          throw new Error("No Business Units assigned to user");
        }

        var baseUrlTree = this.router.createUrlTree(['/', bus[0].slug, 'dashboard']);

        if (!queryParams) {
          // if there are no query params, just send to the first business unit dashboard
          return baseUrlTree;
        }

        // not handle a request that has multiple business units yet, so just send to first one for now
        var action = queryParams['action'];
        console.log("Login redirect action", action, "redirectUrl", queryParams['redirectUrl']);
        if (action == "redirect") {
          var redirectUrl = queryParams['redirectUrl'];
          if (!!redirectUrl && typeof redirectUrl === 'string') {
            // there is a valid redirect URL
            console.log("Redirecting to requested path: " + redirectUrl);
            return this.router.parseUrl(redirectUrl);
          }
          else {
            // no valid redirect URL
            return baseUrlTree;
          }
        }
        else if (action == "deal-interest") {
          var dealId = queryParams['dealId'];
          // the user has to have an investor BU to be sent there!
          var investorBU = bus.filter(x => x.businessUnitType.toLowerCase() == 'investor');

          if (investorBU.length == 0) {
            this.toastrService.error("You don't have access to the requested page. Please contact support.");
            return baseUrlTree;
          }
          else {
            if (investorBU.length > 1) {
              // this is a typical error senario
              console.warn("User has multiple investor business units, which is currently not handled in the redirection logic. Defaulting to first investor BU.", investorBU);
              this.sentryService.captureMessage("User has multiple investor business units, which is currently not handled in the redirection logic. Defaulting to first investor BU.", "warning", 
              {
                investorBU: investorBU
              });
            }

            if (!!dealId) {
              console.log("Redirecting to deal interest page for deal ID: " + dealId);
              return this.router.createUrlTree(['/', investorBU[0].slug, 'deal-preview', dealId], { queryParams: { action: 'deal-interest' } });
            }
            else {
              return baseUrlTree;
            }
          }
        }

        return baseUrlTree;
      })
    )
  }

  public Logout() { 
    this.ResetStoredData();
    this.dialog.closeAll();
    this.http.post(this.BASE_URL + this.LOGOUT_FRAGMENT, {}).subscribe(console.log);
  }

  public ResetUserLoginDetails() {
    this.userLoginDetails = null;
  } 

  private ResetStoredData() {
    this.userLoginDetails = null
    this.appUser = null;
    this.SetToken(null);
  }

  public getUserEmail() : string | null { return this.userLoginDetails?.email || null; }

  public navigateToLogin() : Observable<any> { return from(this.router.navigateByUrl(this.LOGIN_ROUTE)); }
  public navigateToLoginWithNoRedirect() : Observable<any> {
    return from(this.router.navigate([this.LOGIN_ROUTE], { queryParams: { action: 'noredirect' } })); 
  }

  public logoutWithRedirect() : Observable<any> {
    this.Logout();

    var queryParams = { 
      action: 'redirect',
      redirectUrl: this.activatedRoute.snapshot.url.toString() // get the current path to redirect back after login
    };

    // console.log("Logging out user and redirecting to login with query params", queryParams);

    return from(this.router.navigate([this.LOGIN_ROUTE], { queryParams }));
  }
}

